import { SearchIcon } from '@components/Icons/SearchIcon';
import { useIsMobile } from '@lib/hooks/sizing/useIsMobile';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import { Tooltip } from '@mui/material';
import { trackingStore } from '@stores/Tracking';
import { useUIPreferences } from '@stores/UIPreferences';
import classNames from 'classnames';
import React from 'react';
import css from './BaseSelectionBar.module.scss';

interface SelectedCountProps {
  text?: string | ((...args: any[]) => string);
}

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  text: string;
  children?: React.ReactNode;
  tooltip?: string;
}

interface BaseSelectionBarProps {
  selectedCountProps?: SelectedCountProps;
  selectAllProps?: ButtonProps;
  deselectAllProps?: ButtonProps;
  allSelected?: boolean;
  closeProps?: ButtonProps;
  downloadProps?: ButtonProps;
  moveProps?: ButtonProps;
  deleteProps?: ButtonProps;
  askAnAgentProps?: ButtonProps;
  retryProps?: ButtonProps;
  children?: React.ReactNode;
  variant?: 'moment-search' | 'library-search';
}

export const BaseSelectionBar: React.FC<BaseSelectionBarProps> = ({
  selectedCountProps,
  selectAllProps,
  deselectAllProps,
  allSelected,
  closeProps,
  downloadProps,
  moveProps,
  deleteProps,
  askAnAgentProps,
  retryProps,
  children,
  variant = 'library-search',
}) => {
  const uiPreferences = useUIPreferences();
  const isMobile = useIsMobile();

  const getSelectedCountText = () => {
    if (!selectedCountProps?.text) return '';

    if (typeof selectedCountProps.text === 'function') {
      return selectedCountProps.text();
    }

    return selectedCountProps.text;
  };

  const renderButton = (props: ButtonProps | undefined, className: string) => {
    if (!props) return null;

    const { text, children: buttonChildren, tooltip, ...buttonProps } = props;

    // Wrap in a span so MUI Tooltip works on disabled buttons
    // (disabled elements don't fire pointer events)
    return (
      <Tooltip title={tooltip ?? ''} placement="top" arrow>
        <span style={{ display: 'inline-flex' }}>
          <button className={className} {...buttonProps}>
            {buttonChildren || text}
          </button>
        </span>
      </Tooltip>
    );
  };

  return (
    <div
      className={classNames(css.outerContainer, {
        [css.mobileOuterContainer]: isMobile,
      })}
    >
      {variant === 'moment-search' && !isMobile && (
        <Tooltip title="Expand search" placement="top" arrow>
          <button
            className={classNames(css.searchButtonSmallButton)}
            onClick={() => {
              trackingStore.expandSearchClicked();
              uiPreferences.toggleShowSearchBarWhileSelectingMoments();
            }}
          >
            <SearchIcon />
          </button>
        </Tooltip>
      )}
      <div className={css.floatingContainer}>
        <span>{getSelectedCountText()}</span>
        <div className={css.rightSection}>
          {(!isMobile || variant !== 'moment-search') && renderButton(deselectAllProps, css.button)}
          {(!isMobile || variant !== 'moment-search') && renderButton(selectAllProps, css.button)}
          {isMobile &&
            variant === 'moment-search' &&
            selectAllProps &&
            renderButton(
              allSelected && deselectAllProps
                ? { ...deselectAllProps, children: <DoneAllIcon />, tooltip: 'Deselect all' }
                : { ...selectAllProps, children: <DoneAllIcon />, tooltip: 'Select all' },
              css.iconButton,
            )}
          {renderButton(retryProps, css.iconButton)}
          {renderButton(askAnAgentProps, css.iconButton)}
          {renderButton(downloadProps, css.iconButton)}
          {renderButton(moveProps, css.iconButton)}
          {renderButton(deleteProps, css.deleteButton)}
          {(variant !== 'moment-search' || !isMobile) && children}
          {renderButton(closeProps, css.iconButton)}
        </div>
      </div>
    </div>
  );
};
